import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Globe, Heart, Newspaper, Sparkles } from 'lucide-react';
import useInView from '../hooks/useInView';

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/69ab8de2-9056-4863-b71b-4d5b72155bf7/images/810acbfb8d424212c35b7fe1d504594f90cb4b1ac36881dc1cb32a1fdc823cc2.png";

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const About = () => (
  <main className="bg-[#05050A] min-h-screen">
    {/* Hero */}
    <section
      className="relative pt-32 pb-20 overflow-hidden"
      style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050A]/80 via-[#05050A]/70 to-[#05050A]" />
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-purple-600/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <SectionLabel>Our Story</SectionLabel>
        <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05]">
          Built by parents,<br /><span className="gradient-text">for children.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          ROMIND KIDS was created from lived experience — by parents who believe children deserve a safer, more creative, and more emotionally comfortable space in the digital world.
        </p>
      </div>
    </section>

    {/* Founders */}
    <section data-testid="founders-section" className="relative py-24">
      <div className="absolute top-1/2 right-0 w-72 h-72 bg-purple-600/12 rounded-full blur-[100px] pointer-events-none" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The Founders</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-6 tracking-tight">Svetlana & Sergey Lukianov</h2>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <FadeIn delay={100}>
            <div className="glass-card p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <img src="/images/founders/svetlana-lukianova.webp" alt="Svetlana Lukianova" className="w-20 h-24 rounded-2xl object-cover object-top border border-purple-400/20 shadow-lg shadow-purple-500/20" />
                <div>
                  <h3 className="font-outfit font-bold text-white text-lg">Svetlana Lukianova</h3>
                  <p className="text-purple-400 text-sm font-medium">Co-Founder</p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed">
                As a mother of three, Svetlana brings a parent's perspective to every product decision. Her focus is on creating a digital environment that feels emotionally safe — where children can express themselves without the pressures that dominate traditional social platforms.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="glass-card p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <img src="/images/founders/sergey-lukianov.webp" alt="Sergey Lukianov" className="w-20 h-24 rounded-2xl object-cover object-top border border-cyan-400/20 shadow-lg shadow-cyan-500/20" />
                <div>
                  <h3 className="font-outfit font-bold text-white text-lg">Sergey Lukianov</h3>
                  <p className="text-cyan-400 text-sm font-medium">Co-Founder</p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed">
                Sergey's vision is a platform where children aren't just passive consumers but active creators. As a father and product thinker, he shapes ROMIND KIDS as a structured creative ecosystem — not another attention-maximizing network.
              </p>
            </div>
          </FadeIn>
        </div>

        {/* Press & Media */}
        <FadeIn delay={250}>
          <div
            data-testid="founder-press-feature"
            className="relative overflow-hidden rounded-3xl border border-purple-400/20 bg-gradient-to-br from-purple-500/10 via-white/[0.04] to-cyan-500/10 p-8 sm:p-10 mb-16"
          >
            <div className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-purple-500/15 blur-[80px] pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-7">
              <div className="relative w-28 sm:w-32 shrink-0 self-stretch min-h-40 rounded-2xl overflow-hidden border border-purple-300/20 shadow-lg shadow-purple-500/10">
                <img src="/images/press/gulf-magazine-svetlana.webp" alt="Svetlana Lukianova — Gulf Magazine" className="absolute inset-0 w-full h-full object-cover object-top" />
                <div className="absolute bottom-2 right-2 w-8 h-8 rounded-xl bg-black/55 backdrop-blur-md border border-white/10 flex items-center justify-center">
                  <Newspaper size={15} className="text-purple-200" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-outfit font-semibold text-purple-300 tracking-[0.2em] uppercase mb-2">
                  Press &amp; Media · Gulf Magazine
                </p>
                <h3 className="font-outfit font-bold text-white text-xl sm:text-2xl mb-3">
                  Svetlana Lukianova Is Building Systems Across Fragrance, AI and Space
                </h3>
                <p className="text-white/55 text-sm leading-relaxed max-w-3xl mb-4">
                  Gulf Magazine profiles ROMIND KIDS co-founder Svetlana Lukianova and the wider vision connecting children's technology, artificial intelligence, robotics, fragrance, education and SpaceTech.
                </p>
                <p className="text-white/70 text-sm leading-relaxed max-w-3xl mb-3">
                  Although the Gulf Magazine feature tells Svetlana’s story, no journey like this is built alone. Behind her stands a strong partner — her husband and ROMIND KIDS co-founder, Sergey Lukianov — whose constant support, belief and encouragement give her the strength to keep moving forward, building and turning ambitious ideas into reality.
                </p>
                <p className="text-white/50 text-sm leading-relaxed max-w-3xl">
                  For Svetlana, this recognition is not only personal. It also reflects the partnership behind ROMIND KIDS: two people building together, supporting each other, and sharing a long-term vision for what comes next.
                </p>
              </div>
              <a
                href="https://gulfmagazine.co/svetlana-lukianova-is-building-systems/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Read Svetlana Lukianova's Gulf Magazine feature"
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-purple-300/25 bg-purple-500/15 px-5 py-3 text-sm font-semibold text-white hover:bg-purple-500/25 hover:border-purple-300/40 transition-all"
              >
                Read the feature <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </FadeIn>

        {/* Origin Story */}
        <FadeIn delay={100}>
          <div className="glass-card p-8 sm:p-12 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 to-fuchsia-500/20 border border-purple-500/30 flex items-center justify-center">
                <Heart size={16} className="text-purple-400" />
              </div>
              <h3 className="font-outfit font-bold text-white text-xl">Why We Built ROMIND KIDS</h3>
            </div>
            <div className="space-y-4 text-white/55 text-sm leading-relaxed">
              <p>
                As parents of three children, Svetlana and Sergey observed firsthand what the current digital landscape offers children — and what it doesn't. Most social platforms are engineered around engagement metrics, not around the emotional safety and developmental needs of children aged 9 to 14.
              </p>
              <p>
                They watched children navigate chaotic feeds, encounter content designed for adults, and experience the social pressure of likes and follower counts at an age when emotional identity is still forming. They saw parents left with little clarity — trusting in hope rather than in product design.
              </p>
              <p>
                The answer wasn't to restrict children from the digital world. It was to build a better one — a structured, creative, and safer environment where children can communicate, express themselves, build friendships, and grow in a context designed around them.
              </p>
              <p>
                That vision became ROMIND KIDS — the first step in a wider planned ecosystem.
              </p>
            </div>
          </div>
        </FadeIn>

        {/* International vision */}
        <FadeIn delay={200}>
          <div className="glass-card p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center">
                <Globe size={16} className="text-cyan-400" />
              </div>
              <h3 className="font-outfit font-bold text-white text-xl">A Platform Without Borders</h3>
            </div>
            <div className="space-y-4 text-white/55 text-sm leading-relaxed">
              <p>
                One of the most important ideas behind ROMIND KIDS is international connection. Children are naturally curious about the world — other cultures, other languages, other ways of life.
              </p>
              <p>
                ROMIND KIDS is designed to help children connect with peers from other countries in a safe, age-aware environment. Through shared creativity, clubs built around topics and interests, challenges that cross language barriers, and friendship-based communication, children can naturally experience cultural exchange.
              </p>
              <p>
                Not through forced learning. Through friendship, creativity, and the things they love — in a space that protects them while they explore.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>

    {/* Ecosystem & Brand Family */}
    <section data-testid="ecosystem-section" className="relative py-20 bg-[#080714] overflow-hidden">
      <div className="absolute top-0 right-1/4 w-80 h-80 bg-fuchsia-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-cyan-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <SectionLabel>The Ecosystem · Product Line</SectionLabel>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mb-4 tracking-tight max-w-3xl">
            ROMIND KIDS is the first chapter — not the whole book.
          </h2>
          <p className="text-white/55 text-sm leading-relaxed mb-10 max-w-3xl">
            ROMIND KIDS is the public starting point of a wider product family. Each product has its own age logic, emotional purpose, and technology layer — connected by one philosophy: safer digital spaces, meaningful relationships, and AI that supports people instead of exploiting attention.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              name: 'ROMIND KIDS',
              tag: 'Today · in development',
              desc: 'A safer creative social platform for children aged 9–14. It combines posts, clubs, challenges, profiles, Times, parent controls, and the RONI safety layer inside one child-first digital space.',
              accent: 'from-violet-500 to-purple-700',
              ring: 'border-purple-500/30',
              status: 'Release coming soon',
              cta: 'Explore ROMIND KIDS',
              href: '/features',
              external: false,
            },
            {
              name: 'ROMIND TEENS',
              tag: 'Planned · ages 14–18',
              desc: 'A next-generation teen social platform focused on friendship, communication, first feelings, emotional intelligence, and safe relationships — not a dating app, but a protected social environment for older teens.',
              accent: 'from-cyan-500 to-blue-700',
              ring: 'border-cyan-500/25',
              status: 'Planned',
              cta: 'Preview concept',
              href: null,
              external: false,
            },
            {
              name: 'ROMIND Personal Companion',
              tag: 'In development · AI companion',
              desc: 'A personal AI companion for adult use: emotional dialogue, memory, planning, creative support, daily assistance, personal safety logic, and a long-term foundation for a future physical robotic companion.',
              accent: 'from-blue-500 to-indigo-700',
              ring: 'border-blue-500/25',
              status: 'Core in development',
              cta: 'Visit ROMIND Tech',
              href: 'https://romind.tech',
              external: true,
            },
            {
              name: 'ScentUnivers™',
              tag: 'In development · sensory internet',
              desc: 'A scent-based digital ecosystem connecting ScentSync, Air Dot, ScentMessenger, ScentMap, ScentBridge, ROMIND, and AI perfume tools — designed for aromatic messages, sensory presence, and future scent-enabled communication.',
              accent: 'from-fuchsia-500 to-pink-700',
              ring: 'border-fuchsia-500/25',
              status: 'In development',
              cta: 'Ecosystem preview',
              href: null,
              external: false,
            },
          ].map((b, i) => (
            <FadeIn key={b.name} delay={i * 100}>
              <div data-testid={`ecosystem-card-${i}`} className={`glass-card p-6 h-full border ${b.ring} flex flex-col`}>
                <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${b.accent} flex items-center justify-center mb-4 shadow-lg`}>
                  <Sparkles size={17} className="text-white" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-outfit font-semibold text-white/40 tracking-[0.18em] uppercase">{b.status}</span>
                </div>
                <h3 className="font-outfit font-bold text-white text-lg mb-1">{b.name}</h3>
                <p className="text-purple-300/80 text-[11px] font-medium uppercase tracking-widest mb-3">{b.tag}</p>
                <p className="text-white/55 text-xs leading-relaxed flex-1">{b.desc}</p>
                {b.href && (
                  b.external ? (
                    <a
                      href={b.href}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10 transition-all"
                    >
                      {b.cta} <ArrowRight size={13} />
                    </a>
                  ) : (
                    <Link
                      to={b.href}
                      className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10 transition-all"
                    >
                      {b.cta} <ArrowRight size={13} />
                    </Link>
                  )
                )}
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={400}>
          <p className="text-white/35 text-xs leading-relaxed mt-8 max-w-3xl">
            ROMIND KIDS and ROMIND Personal Companion are already in active development. ROMIND TEENS is planned as a dedicated older-teen environment. ScentUnivers™ is being developed as a separate sensory-technology ecosystem connected to the broader ROMIND vision.
          </p>
        </FadeIn>
      </div>
    </section>

    {/* Mission */}
    <section className="py-16 bg-[#080714]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <blockquote className="font-outfit font-bold text-2xl sm:text-3xl text-white/90 leading-relaxed italic mb-8">
            "We didn't build ROMIND KIDS to compete with social media.<br />We built it to offer children something it never was —<br />
            <span className="gradient-text not-italic">a place actually made for them.</span>"
          </blockquote>
          <p className="text-white/40 text-sm">— Svetlana & Sergey Lukianov, Co-Founders</p>
        </FadeIn>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/features" data-testid="about-explore-features" className="px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all text-center">
          Explore the Platform
        </Link>
        <Link to="/contact" data-testid="about-contact" className="px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-all text-center flex items-center justify-center gap-2">
          Get in Touch <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  </main>
);

export default About;
